import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';

const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=';

export class SignaturePadController extends InjectableClass {
  constructor(
    $window,
    $timeout,
    $scope,
    $element,
    $translate,
    SignaturePad,
    signaturePadService,
    cropService,
    pollingService,
    errorTrackingService
  ) {
    'ngInject';
    super({
      $window,
      $timeout,
      $scope,
      $element,
      $translate,
      SignaturePad,
      signaturePadService,
      cropService,
      pollingService,
      errorTrackingService,
    });
  }

  get context() {
    return this.canvas.getContext('2d');
  }

  get canvas() {
    return this.signaturePadCanvas._canvas;
  }

  get mobileUrlWithToken() {
    return this.mobileSignUrl + this.token + `?theme=${this.theme}`;
  }

  get translateParams() {
    return { serviceName: this.serviceName };
  }

  $onInit() {
    this.$translate.preferredLanguage('de');
    this.$translate.use('de');

    _.defaults(this, {
      dataUrl: EMPTY_IMAGE,
      clearable: true,
      clearLabel: 'reset',
    });

    this.manualCanvas = null;
  }

  initCanvas() {
    this.signaturePadCanvas = new this.SignaturePad(
      this.$element.find('canvas')[0],
      {
        onBegin: () => {
          this.setDirty();
        },
        onEnd: () => {
          this.updateModel();
        },
      }
    );

    this.signatureFile = this.$element.find('input')[0];
    this.signatureFile.onchange = (event) => {
      this.upload(event.target.files[0]);
      event.target.value = '';
    };

    // Trigger resize for initial size fitting
    this.$timeout(this.onResize.bind(this), 500);

    this.bindEventListeners();
  }

  onCroppingUploadError() {
    this.croppingUploadError = true;
  }

  upload(file) {
    this.generate = false;
    this.croppingUploadError = false;

    const reader = new FileReader();
    // just read the files content and draw it on the canvas
    reader.onload = (e) => {
      const signatureImage = e.target.result;
      if (!signatureImage.includes('data:image')) {
        const fileType = _.isString(signatureImage)
          ? signatureImage.match(/.*base64,/g)[0]
          : signatureImage;
        this.errorTrackingService.captureMessage(
          `Upload Signature ${fileType}`
        );
      }
      this.uploadedOriginal = signatureImage;
      this.cropService.openModal(this.$scope);
    };

    reader.readAsDataURL(file);
  }

  cropImage() {
    this.drawNewImage(this.croppedImage);
    this.closeCropModal();
  }

  updateModel() {
    this.$timeout().then(() => {
      let signature = {};

      if (this.signaturePadCanvas.isEmpty()) {
        signature.dataUrl = EMPTY_IMAGE;
        signature.isEmpty = true;
      } else {
        const { height, width } = this.canvas;

        // Overwrite data url with cropped version if auto-crop is enabled
        if (this.autoCrop) {
          signature.dataUrl = this.signaturePadService.getCroppedSignature(
            width,
            height,
            this.context,
            this.skipCheckAspectRatio
          );
        } else {
          signature.dataUrl = this.signaturePadCanvas.toDataURL();
        }

        signature.isEmpty = false;
        signature.isCropped = this.autoCrop;
        signature.size = { height, width };
      }

      this.dataUrl = signature.dataUrl;
      this.model = signature;
    });
  }

  uploadSignature() {
    const manualCanvas = this.manualCanvas;
    this.manualCanvas = false;
    if (manualCanvas === null) {
      this.initCanvas();
    }
    this.signatureFile.click();
  }

  manualSignature() {
    const manualCanvas = this.manualCanvas;
    this.manualCanvas = true;
    if (manualCanvas === null) {
      this.initCanvas();
    }
  }

  clear() {
    this.manualCanvas = true;
    this.signaturePadCanvas.clear();
    this.dataUrl = undefined;
    this.updateModel();
    this.setDirty(false);
  }

  setDirty(dirty = true) {
    if (dirty) {
      this.$element.addClass('dirty');
    } else {
      this.$element.removeClass('dirty');
    }
  }

  isEmpty() {
    return !this.model || this.model.isEmpty;
  }

  getSignature() {
    const { canvas } = this;
    return this.context.getImageData(0, 0, canvas.width, canvas.height);
  }

  restoreSignature(signature) {
    this.context.putImageData(signature, 0, 0);
    this.updateModel();
  }

  bindEventListeners() {
    const { $window, $scope } = this;

    const win = angular.element($window);
    const resizeCanvas = this.onResize.bind(this);
    win.bind('orientationchange', resizeCanvas);
    win.bind('resize', resizeCanvas);

    $scope.$watch('dataUrl', (dataUrl) => {
      if (angular.isDefined(dataUrl) && dataUrl) {
        this.signaturePadCanvas.fromDataURL(dataUrl);
      }
    });

    $scope.$watch('isVisible', (visible) => {
      if (angular.isDefined(visible) && visible) {
        resizeCanvas();
      }
    });

    $scope.$on('$destroy', () => {
      win.unbind('orientationchange', resizeCanvas);
      win.unbind('resize', resizeCanvas);
    });
  }

  onResize(restoreSignature = true) {
    let ratio, signature;

    if (restoreSignature) {
      signature = this.getSignature();
    }

    ratio = Math.max(this.$window.devicePixelRatio || 1, 1);
    this.canvas.width = this.canvas.offsetWidth * ratio;
    this.canvas.height = this.canvas.offsetHeight * ratio;
    this.context.scale(ratio, ratio); // TODO do we really need this?

    this.dataUrl = null;

    if (restoreSignature) {
      this.restoreSignature(signature);
    }
  }

  // only called from markup
  generateSignature() {
    if (!this.signaturePadCanvas._isEmpty) {
      // prevent overwriting generated signature multiple times.
      return;
    }

    this.signaturePadService.generateSignature(this.canvas, this.clientName);
    this.signaturePadCanvas._isEmpty = false;
    this.updateModel();
  }

  /*
    With opening modal we start polling against sign service until sign service returns signature
   */
  openModal() {
    const manualCanvas = this.manualCanvas;
    this.manualCanvas = false;
    if (manualCanvas === null) {
      this.initCanvas();
    }

    if (!this.serviceUrl) {
      return;
    }
    this.token = '';

    this.$scope.serviceUrl = this.serviceUrl;
    this.pollingService.openModalPolling(this.$scope, (receivedSignature) => {
      this.clear();
      this.drawNewImage(receivedSignature);
      /*
      this.signaturePadService.fitReceivedSignatureToSignaturePad(
        this.canvas,
        receivedSignature,
        (base64) => {
          this.dataUrl = base64;
          this.drawNewImage(base64);
        }
      );*/
    });
  }

  closeModal() {
    this.pollingService.closeModal();
  }

  closeCropModal() {
    this.cropService.closeModal();
  }

  drawViaSMS() {
    if (!this.smsSubmitForm.$valid) {
      return;
    }

    this.loadingSMS = true;
    this.pollingService
      .sendSms(
        this.mobileUrlWithToken,
        {
          mobileNumber: this.model.mobileNumber,
          sender: this.serviceName,
          // message: this.$translate.instant('COMPONENTS.SIGNATURE.SIGN_MESSAGE')
        },
        this.serviceUrl
      )
      .then(() => {
        this.smsSent = true;
      })
      .finally(() => {
        this.loadingSMS = false;
      });
  }

  drawNewImage(dataUrl) {
    // keep picture dimensions.
    let sign = new Image();
    sign.onload = () => {
      this.drawImageScaled(sign, this.context);
      this.signaturePadCanvas._isEmpty = false;
      this.updateModel();
      this.onResize(true);
    };
    sign.src = dataUrl;
  }

  drawImageScaled(img, ctx) {
    const canvas = ctx.canvas;
    const hRatio = canvas.clientWidth / img.width;
    const vRatio = canvas.clientHeight / img.height;
    const ratio = Math.min(hRatio, vRatio);
    const centerShift_x = (canvas.clientWidth - img.width * ratio) / 2;
    const centerShift_y = (canvas.clientHeight - img.height * ratio) / 2;
    ctx.drawImage(
      img,
      0,
      0,
      img.width,
      img.height,
      centerShift_x,
      centerShift_y,
      img.width * ratio,
      img.height * ratio
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/@fino/ng1-signature-pad/src/signaturePad.controller.js